<script setup lang="ts">
/**
 * A month's label under a twelve-month chart, which opens that month by day.
 * Marked like an overridden node in the hierarchy — orange, with an orange
 * left border — when the target sets days of its own in it, and with a red
 * dot when something on those days is wrong.
 */
defineProps<{
	label: string;
	/** Full month name, for the tooltip. */
	name: string;
	marked?: boolean;
	error?: string | null;
	bold?: boolean;
	/** Why it can't open right now, when it can't. */
	disabledReason?: string | null;
	markedHint?: string;
}>();

const emit = defineEmits<{ open: [] }>();
</script>

<template>
	<button
		class="month-link text-caption mt-2"
		:class="{ 'font-weight-bold': bold, 'month-link--marked': marked }"
		:disabled="!!disabledReason"
		:title="
			disabledReason ??
			(marked && markedHint ? `Open ${name} by day — ${markedHint}` : `Open ${name} by day`)
		"
		@click="emit('open')"
	>
		{{ label }}
		<v-icon icon="fa-light fa-chevron-right" size="8" />
		<span v-if="error" class="month-link-error" :title="error" />
	</button>
</template>

<style scoped lang="scss">
.month-link {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 3px;
	border: 0;
	background: transparent;
	color: inherit;
	cursor: pointer;
	padding: 2px 0;
	border-left: 3px solid transparent;
	text-decoration: underline dotted;
	text-underline-offset: 3px;
}

.month-link:disabled {
	cursor: default;
	text-decoration: none;
	opacity: 0.6;
}

// Same marker as PlanHierarchyRow.vue's override border. Every label carries
// the border, transparent when unmarked, so marking one never shifts it.
.month-link--marked {
	border-left-color: rgb(var(--v-theme-warning));
	color: rgb(var(--v-theme-warning));
}

// Same marker PlanNodeDetailPanel.vue puts on a tab with a problem.
.month-link-error {
	width: 7px;
	height: 7px;
	border-radius: 50%;
	background: rgb(var(--v-theme-error));
	flex: 0 0 auto;
}
</style>
